/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useEffect, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

import isInputCorrect from "src/utils/ResultValidationHelper";

import {
  queryInspectionModelItems,
  queryInspectionResults,
} from "src/services/AmplifyQuery";

import Card from "src/components/cards/Card.react";
import ResultBadge from "src/components/badges/Result.react";
import ChildTypeBadge from "src/components/badges/ChildType.react";
import ResultDropdown from "src/components/dropdowns/Result.react";

import {
  Table,
  ColHeader,
  TableBody,
  Col,
} from "src/components/tables/Table.react";
import EditInspectionResultSlideover from "src/components/slideovers/EditInspectionResult.react";
import EditObservationSlideover from "src/components/slideovers/EditObservation.react";

import CustomResults from "src/views/report/Custom.react";

/**
 * The Result component
 *
 * @author Thais Watanabe
 */
const Result = (props) => {
  const {
    inspectionModel,
    turbineInspection,
    lastTurbineInspections,
    inspection,
    print,
  } = props;

  const location = useLocation();

  const [inspectionModelItems, setInspectionModelItems] = useState([]);
  const [lastResults, setLastResults] = useState([]);
  const [results, setResults] = useState([]);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isObsOpen, setIsObsOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);

  /* useEffect */
  useEffect(() => {
    const hash = location.hash;

    if (inspectionModelItems) {
      // const offset = document.getElementById(hash?.substring(1))?.offsetTop;
      let rect = document
        .getElementById(hash?.substring(1))
        ?.getBoundingClientRect();
      window.scrollTo({
        top: rect?.top,
        behavior: "smooth",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionModelItems]);

  /* useEffect */
  useEffect(() => {
    fetchInspectionModelItems();

    return () => {
      setInspectionModelItems([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * fetchInspectionModelItems
   */
  async function fetchInspectionModelItems() {
    const filter = {
      modelNumber: { eq: inspectionModel.modelNumber },
    };
    queryInspectionModelItems(filter, setInspectionModelItems);
  }

  /* useEffect */
  useEffect(() => {
    fetchInspectionResults();

    return () => {
      setResults([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * fetchInspectionResults
   */
  async function fetchInspectionResults() {
    const filter = {
      turbineInspectionID: { eq: turbineInspection.id },
      itemId: { beginsWith: inspectionModel.modelNumber },
    };

    queryInspectionResults(filter, setResults);
  }

  /* useEffect */
  useEffect(() => {
    fetchLastInspectionResults();

    return () => {
      setLastResults([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * fetchLastInspectionResults
   */
  async function fetchLastInspectionResults() {
    const inspectionIds = lastTurbineInspections.map((obj) => obj.id);

    if (inspectionIds.length > 0) {
      const filter = {
        or: [],
        itemId: { beginsWith: inspectionModel.modelNumber },
      };
      inspectionIds.forEach((id) =>
        filter.or.push({ turbineInspectionID: { eq: id } })
      );

      queryInspectionResults(filter, setLastResults);
    }
  }

  /**
   * showResult
   * @param {*} itemId
   * @returns
   */
  function showResult(itemId) {
    return results.filter((result) => result.itemId === itemId)[0];
  }

  /**
   * showLastResult1
   * @param {*} itemId
   * @returns
   */
  function showLastResult1(itemId) {
    const id = lastTurbineInspections.map((obj) => obj.id)[0];

    return lastResults.filter(
      (result) => result.itemId === itemId && result.turbineInspectionID === id
    )[0];
  }

  /**
   * showLastResult2
   * @param {*} itemId
   * @returns
   */
  function showLastResult2(itemId) {
    if (lastTurbineInspections.length > 1) {
      const id = lastTurbineInspections.map((obj) => obj.id)[1];

      return lastResults.filter(
        (result) =>
          result.itemId === itemId && result.turbineInspectionID === id
      )[0];
    } else {
      return;
    }
  }

  /**
   * openEdit
   * @param {*} inspectionModelItem
   * @param {*} inspectionResult
   */
  function openEdit(inspectionModelItem, inspectionResult) {
    if (!inspectionResult) {
      toast.error("修正できません");
    } else {
      setSelectedItem(inspectionModelItem);
      setSelectedResult(inspectionResult);
      setIsEditOpen(!isEditOpen);
    }
  }

  /**
   * openObs
   * @param {*} inspectionModelItem
   * @param {*} inspectionResult
   */
  function openObs(inspectionModelItem, inspectionResult) {
    if (!inspectionResult) {
      toast.error("データがありません");
    } else {
      setSelectedItem(inspectionModelItem);
      setSelectedResult(inspectionResult);
      setIsObsOpen(!isObsOpen);
    }
  }

  return (
    <div className="pagebreak print:text-xxs">
      <Card title={inspectionModel.name}>
        {inspectionModel.modelNumber !== 14 && (
          <Table>
            <thead className="table-header-group">
              {print && (
                <>
                  <tr className="print:table-row">
                    <th
                      rowSpan={2}
                      colSpan={2}
                      className="p-0.5 z-20 overflow-y-auto h-5 top-0 border border-black text-center bg-gray-300 sticky"
                    >
                      {inspectionModel.name}
                    </th>
                    <th
                      rowSpan={2}
                      className="p-0.5 z-20 overflow-y-auto h-5 top-0 border border-black text-center bg-gray-300 sticky"
                    >
                      {inspection.windFarm.name}
                    </th>
                    <th
                      rowSpan={2}
                      className="p-0.5 z-20 overflow-y-auto h-5 top-0 border border-black text-center bg-gray-300 sticky"
                    >
                      {inspection.name}
                    </th>
                    <th
                      colSpan={2}
                      className="p-0.5 z-20 overflow-y-auto h-5 top-0 border border-black text-center bg-gray-300 sticky"
                    >
                      号機
                    </th>
                    <th
                      colSpan={3}
                      className="p-0.5 z-20 overflow-y-auto h-5 top-0 border border-black text-center bg-gray-300 sticky"
                    >
                      {turbineInspection.windTurbine.tower}号機
                    </th>
                  </tr>
                  <tr className="print:table-row">
                    <th
                      colSpan={2}
                      className="p-0.5 z-20 overflow-y-auto h-5 top-0 border border-black text-center bg-gray-300 sticky"
                    >
                      点検責任者
                    </th>
                    <th
                      colSpan={3}
                      className="p-0.5 z-20 overflow-y-auto h-5 top-0 border border-black text-center bg-gray-300 sticky"
                    >
                      {inspection.responsible}
                    </th>
                  </tr>
                </>
              )}

              <tr>
                {!print && (
                  <ColHeader
                    className="z-10 overflow-y-auto h-5 sticky top-0 text-center w-1 print:hidden"
                    rowSpan={2}
                  ></ColHeader>
                )}
                <ColHeader
                  className="text-center z-10 overflow-y-auto h-5 sticky top-0 w-14 print:w-8 print:relative print:z-0"
                  rowSpan={2}
                >
                  作業
                  <br />
                  番号
                </ColHeader>
                <ColHeader
                  className="text-center z-10 overflow-y-auto h-5 sticky top-0 w-28 print:w-16 print:relative print:z-0"
                  rowSpan={2}
                >
                  項目
                </ColHeader>
                <ColHeader
                  className="text-center z-10 overflow-y-auto h-5 sticky top-0 w-64 print:relative print:z-0"
                  colSpan={2}
                  rowSpan={2}
                >
                  点検内容
                </ColHeader>
                <ColHeader
                  className="z-10 overflow-y-auto h-5 sticky top-0 text-center w-28 print:w-14 print:relative print:z-0"
                  rowSpan={2}
                >
                  結果
                </ColHeader>
                <ColHeader
                  className="z-10 overflow-y-auto h-5 sticky top-0 text-center w-28 print:w-14 print:relative print:z-0"
                  rowSpan={2}
                >
                  作業日
                </ColHeader>
                <ColHeader
                  className="z-10 overflow-y-auto h-5 sticky top-0 text-center w-20 print:relative print:z-0"
                  colSpan={2}
                >
                  過去点検結果
                </ColHeader>
                <ColHeader
                  className="z-10 overflow-y-auto h-5 sticky top-0 text-center w-28 print:w-14 print:relative print:z-0"
                  rowSpan={2}
                >
                  分類
                </ColHeader>
              </tr>
              <tr className="divide-x divide-gray-200 print:divide-black">
                <ColHeader className="z-10 overflow-y-auto h-5 sticky top-10 text-center w-28 border border-gray-200 print:overflow-auto print:relative print:z-0 print:top-0 print:w-14">
                  {lastTurbineInspections?.[0]?.inspection?.name ?? "前回結果1"}
                </ColHeader>
                <ColHeader className="z-10 overflow-y-auto h-5 sticky top-10 text-center w-28 border border-gray-200 print:overflow-auto print:relative print:z-0 print:top-0 print:w-14">
                  {lastTurbineInspections?.[1]?.inspection?.name ?? "前回結果2"}
                </ColHeader>
              </tr>
            </thead>
            {/* <TableBody> */}
            {inspectionModelItems.length < 1 ? (
              <TableBody>
                <tr>
                  <Col colSpan={10}>読み込み中…</Col>
                </tr>
              </TableBody>
            ) : (
              inspectionModelItems
                .sort((a, b) =>
                  a.itemModelNumber > b.itemModelNumber ? 1 : -1
                )
                .map(
                  (modelItem, i) =>
                    !modelItem.isSpecialReport && (
                      <TableBody key={"i" + i}>
                        {modelItem.inspectionModelItemChildren.items
                          .sort((a, b) =>
                            a.itemChildNumber > b.itemChildNumber ? 1 : -1
                          )
                          .map(
                            (itemChild, y) =>
                              !itemChild.isSpecialReport && (
                                <tr key={"y" + y}>
                                  {y === 0 && (
                                    <>
                                      {!print && (
                                        <Col
                                          id={modelItem.name}
                                          rowSpan={
                                            modelItem
                                              .inspectionModelItemChildren.items
                                              .length
                                          }
                                          className="print:hidden"
                                        >
                                          <ResultDropdown
                                            openEdit={openEdit}
                                            openObs={openObs}
                                            turbineInspection={
                                              turbineInspection
                                            }
                                            selectedItem={modelItem}
                                            selectedResult={showResult(
                                              modelItem.name
                                            )}
                                            lastTurbineInspections={
                                              lastTurbineInspections
                                            }
                                            inspection={inspection}
                                          />
                                        </Col>
                                      )}
                                      <Col
                                        rowSpan={
                                          modelItem.inspectionModelItemChildren
                                            .items.length
                                        }
                                        className="text-center"
                                      >
                                        {modelItem.name}
                                      </Col>
                                      <Col
                                        rowSpan={
                                          modelItem.inspectionModelItemChildren
                                            .items.length
                                        }
                                        className="text-center"
                                      >
                                        {modelItem.description}
                                      </Col>
                                    </>
                                  )}
                                  {itemChild.title && (
                                    <Col
                                      rowSpan={itemChild.rowSpan}
                                      className="print:w-32"
                                    >
                                      {itemChild.title}
                                    </Col>
                                  )}
                                  {itemChild.rowSpan === 1 &&
                                  itemChild.title === "" ? (
                                    <Col colSpan={2}>{itemChild.name}</Col>
                                  ) : (
                                    <Col>{itemChild.name}</Col>
                                  )}
                                  <Col className="text-center">
                                    {showResult(modelItem.name)
                                      ? showResult(modelItem.name)
                                          .childrenResults.length > 0 && (
                                          <ResultBadge
                                            result={
                                              showResult(modelItem.name)
                                                .childrenResults[y]
                                            }
                                            unit={itemChild.unit}
                                            isInputCorrect={isInputCorrect(
                                              modelItem.name,
                                              showResult(modelItem.name)
                                                .childrenResults[y],
                                              y
                                            )}
                                          />
                                        )
                                      : "-"}
                                  </Col>
                                  <Col className="text-center">
                                    {showResult(modelItem.name)
                                      ? moment(
                                          showResult(modelItem.name).createdAt
                                        ).format("YYYY-MM-DD")
                                      : "-"}
                                  </Col>
                                  <Col className="text-center">
                                    {showLastResult1(modelItem.name)
                                      ? showLastResult1(modelItem.name)
                                          .childrenResults.length > 0 && (
                                          <ResultBadge
                                            result={
                                              showLastResult1(modelItem.name)
                                                .childrenResults[y]
                                            }
                                            unit={itemChild.unit}
                                            isInputCorrect={isInputCorrect(
                                              modelItem.name,
                                              showLastResult1(modelItem.name)
                                                .childrenResults[y],
                                              y
                                            )}
                                          />
                                        )
                                      : "-"}
                                  </Col>
                                  <Col className="text-center">
                                    {showLastResult2(modelItem.name)
                                      ? showLastResult2(modelItem.name)
                                          .childrenResults.length > 0 && (
                                          <ResultBadge
                                            result={
                                              showLastResult2(modelItem.name)
                                                .childrenResults[y]
                                            }
                                            unit={itemChild.unit}
                                            isInputCorrect={isInputCorrect(
                                              modelItem.name,
                                              showLastResult2(modelItem.name)
                                                .childrenResults[y],
                                              y
                                            )}
                                          />
                                        )
                                      : "-"}
                                  </Col>
                                  <Col>
                                    <span className="flex flex-col gap-1 justify-center items-center">
                                      {itemChild.isPeriodicInspection && (
                                        <ChildTypeBadge type="定期点検" />
                                      )}
                                      {itemChild.isBusinessInspection && (
                                        <ChildTypeBadge type="事業者検査" />
                                      )}
                                      {itemChild.isInternalUse && (
                                        <ChildTypeBadge type="社内" />
                                      )}
                                    </span>
                                  </Col>
                                </tr>
                              )
                          )}
                      </TableBody>
                    )
                )
            )}
            {/* </TableBody> */}
          </Table>
        )}
      </Card>

      {inspectionModelItems.length > 1 && (
        <CustomResults
          inspection={inspection}
          turbineInspection={turbineInspection}
          inspectionModel={inspectionModel}
          inspectionModelItems={inspectionModelItems.filter((obj) =>
            obj.inspectionModelItemChildren.items.some((e) => e.isSpecialReport)
          )}
          results={results.filter((obj) =>
            inspectionModelItems
              .filter((obj) =>
                obj.inspectionModelItemChildren.items.some(
                  (e) => e.isSpecialReport
                )
              )
              .map(({ name }) => name)
              .includes(obj.itemId)
          )}
          lastResults={lastResults}
          lastTurbineInspections={lastTurbineInspections}
          openEdit={openEdit}
          openObs={openObs}
          print={print}
        />
      )}

      <EditInspectionResultSlideover
        isEditOpen={isEditOpen}
        updateEdit={setIsEditOpen}
        inspectionModelItem={selectedItem}
        inspectionResult={selectedResult}
      />

      <EditObservationSlideover
        isObsOpen={isObsOpen}
        setIsObsOpen={setIsObsOpen}
        inspectionModelItem={selectedItem}
        inspectionResult={selectedResult}
      />
    </div>
  );
};

export default Result;
